import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { toastr } from 'react-redux-toastr';
import { Input } from "reactstrap";
import general_fetch from '../../utils/fetch';
import { derive_tds_percentage } from "../EditTdsModal/index.jsx";

/**
 * Inline TDS adjuster used inside the two CS-facing payment-confirmation modals:
 *  - MakePayment (Dues tab)
 *  - MarkAsPaidModal (Mid Cycle Payments)
 *
 * Shares the same controller / endpoint as EditTdsModal (csp/apply_tds_to_order).
 *
 * NOTHING is persisted until the record button is clicked (CSP-71): changing the
 * TDS only updates a client-side preview — it reports the recomputed { total, tds }
 * up to the modal via on_preview (which live-updates the payment breakup and the
 * amount field, mirroring EditTdsModal's "New breakup"). The actual csp/apply_tds_to_order
 * call happens only when the record button flushes this adjuster (flush(), below),
 * so cancelling / closing before recording takes no permanent action.
 *
 * TDS can be entered as a percentage rate OR a fixed (absolute) amount via the
 * Percentage / Fixed toggle. The two are mutually exclusive on the backend.
 *
 * Props:
 *  - order: full order object (id, tds, tds_percentage, tds_absolute, price_after_discount_before_tax, tax, total, currency).
 *  - store_info: { country, market, ... } — drives India gating + currency symbol.
 *  - user_details: { role: { store: [...] } } — drives role gating.
 *  - has_pending_offline_payment: boolean — gates the controls (defense-in-depth; backend re-checks).
 *  - set_page_loader: existing global loader (set_page_loader(true, msg) / set_page_loader(false)).
 *  - on_preview: callback invoked on every change with the previewed { total, tds, tds_absolute,
 *    tds_percentage } (or the order's own values when nothing has changed) so the caller can
 *    live-update its amount field and breakup. No API involved.
 *  - show_breakup: render the compact Subtotal/Tax/TDS/Total block above the adjust row (default true).
 *    Callers that already render their own breakup (MarkAsPaidModal) pass false.
 *
 * Ref (imperative handle):
 *  - flush(): Promise<{ applied, order }> — called by the record button before it records.
 *    Applies the pending TDS via csp/apply_tds_to_order (order = the recomputed order), or
 *    resolves { applied:false } when nothing changed; rejects if the apply failed.
 */

const PRESETS = [
    { value: 0, label: 'No TDS (0%)' },
    { value: 2, label: 'Technical fees (2%)' },
    { value: 10, label: 'Professional fees (10%)' },
    { value: 'custom', label: 'Custom...' },
];

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

const fmt = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const InlineTdsAdjuster = forwardRef(({
    order,
    store_info,
    user_details,
    has_pending_offline_payment,
    set_page_loader,
    on_preview,
    show_breakup = true,
}, ref) => {
    const current_percentage = derive_tds_percentage(order);
    const current_absolute = Number(order && order.tds_absolute) || 0;
    const [mode, set_mode] = useState('percentage');
    const [selected, set_selected] = useState(null);
    const [custom_value, set_custom_value] = useState('');
    const [absolute_value, set_absolute_value] = useState('');
    const [applying, set_applying] = useState(false);

    // The in-flight record-time apply promise, so a double record click doesn't fire twice.
    const inflight_ref = useRef(null);
    // Always points at the latest run_apply so the imperative flush() closes over fresh state.
    const run_apply_ref = useRef(null);
    // Always points at the latest on_preview so the report effect calls the latest without
    // making the parent's inline callback a dependency (which would re-fire every render).
    const on_preview_ref = useRef(null);

    // Re-sync the controls whenever the order ref changes (e.g. after a successful apply
    // refetches and pushes a new order with updated tds in).
    useEffect(() => {
        if (current_absolute > 0) {
            set_mode('absolute');
            set_absolute_value(String(current_absolute));
            set_selected(0);
            set_custom_value('');
        } else {
            set_mode('percentage');
            set_absolute_value('');
            const matching_preset = PRESETS.find(p => typeof p.value === 'number' && p.value === current_percentage);
            if (matching_preset) {
                set_selected(matching_preset.value);
                set_custom_value('');
            } else {
                set_selected('custom');
                set_custom_value(String(current_percentage));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order && order.id, current_percentage, current_absolute]);

    // Gating: India-only + CS role + order exists.
    const is_india = store_info && (store_info.country === 'IN' || store_info.market === 'india');
    const role_store = user_details && user_details.role && user_details.role.store ? user_details.role.store : [];
    const can_edit = role_store.includes('customer_success_admin') || role_store.includes('customer_success_executive');
    const editable = !!(order && order.id && is_india && can_edit);

    const padbt = Number(order && order.price_after_discount_before_tax || 0);

    const chosen_percentage = (() => {
        if (selected === 'custom') {
            const n = Number(custom_value);
            if (Number.isNaN(n) || custom_value === '') return null;
            return n;
        }
        if (selected === null) return null;
        return Number(selected);
    })();

    const chosen_absolute = (() => {
        const n = Number(absolute_value);
        if (Number.isNaN(n) || absolute_value === '') return null;
        return n;
    })();

    const valid_percentage = chosen_percentage !== null
        && !Number.isNaN(chosen_percentage)
        && chosen_percentage >= 0
        && chosen_percentage <= 100;

    // Absolute TDS must be >= 0 and can't exceed the discounted, pre-tax amount.
    const valid_absolute = chosen_absolute !== null
        && !Number.isNaN(chosen_absolute)
        && chosen_absolute >= 0
        && chosen_absolute <= padbt;

    const is_valid = mode === 'absolute' ? valid_absolute : valid_percentage;

    // For an order that already carries a fixed (absolute) TDS, derive_tds_percentage
    // back-computes a spurious non-zero rate from the amount (legacy rows where the
    // tds_percentage column is null). Treat the current percentage as 0 in that case so an
    // untouched fixed-TDS order isn't seen as "changed" and doesn't fire a no-op apply.
    const current_percentage_effective = current_absolute > 0 ? 0 : current_percentage;
    const has_changed = mode === 'absolute'
        ? (chosen_absolute !== null && (Number(chosen_absolute) !== current_absolute || current_percentage_effective > 0))
        : (chosen_percentage !== null && (Number(chosen_percentage) !== current_percentage || current_absolute > 0));

    // Client-side preview of the recompute. Mirrors backend pricing util:
    //   percentage: tds = price_after_discount_before_tax * pct / 100
    //   absolute:   tds = the entered amount
    //   total = (price_after_discount_before_tax - tds) + tax
    const preview_tds = (() => {
        if (!is_valid) return null;
        return mode === 'absolute' ? round2(chosen_absolute) : round2((padbt * chosen_percentage) / 100);
    })();

    const preview_total = (() => {
        if (preview_tds === null) return null;
        const tax_pct = padbt > 0 ? round2((Number((order && order.tax) || 0) / padbt) * 100) : 0;
        const tax = round2((padbt * tax_pct) / 100);
        return round2(padbt - preview_tds + tax);
    })();

    // What the modal's amount field + breakup should show right now. Only a valid, changed,
    // editable value shows the client preview; otherwise the order's own (exact backend)
    // values, so an unchanged record still sends the exact due the backend enforces.
    const has_valid_change = editable && is_valid && has_changed;
    const effective_total = has_valid_change ? preview_total : Number((order && order.total) || 0);
    const effective_tds = has_valid_change ? preview_tds : Number((order && order.tds) || 0);
    const effective_tds_absolute = has_valid_change
        ? (mode === 'absolute' ? Number(chosen_absolute) : 0)
        : (Number(order && order.tds_absolute) || 0);
    const effective_tds_percentage = has_valid_change
        ? (mode === 'percentage' ? Number(chosen_percentage) : 0)
        : derive_tds_percentage(order);

    // Report the preview up so the amount field + breakup live-update — no API. Keyed on the
    // primitive effective values (not the callback identity) to avoid a re-render loop.
    on_preview_ref.current = on_preview;
    useEffect(() => {
        // Only emit for a real order — otherwise a null/absent order (e.g. MakePayment's
        // active_due before it resolves) would report {total:0} and reset the caller's
        // amount field to 0.
        if (order && order.id && typeof on_preview_ref.current === 'function') {
            on_preview_ref.current({
                total: effective_total,
                tds: effective_tds,
                tds_absolute: effective_tds_absolute,
                tds_percentage: effective_tds_percentage,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order && order.id, effective_total, effective_tds, effective_tds_absolute, effective_tds_percentage]);

    const currency_symbol = (store_info && store_info.country !== 'IN') ? '$' : '₹';

    // Apply the currently-entered TDS via the backend. Returns { applied, order } (order =
    // the recomputed order) or { applied:false } when there's nothing to do; throws on
    // failure after a descriptive toast. Only reached through the record button's flush() —
    // never on change/blur — so no permanent action is taken before the user records.
    const run_apply = () => {
        if (!editable || !is_valid || !has_changed) return Promise.resolve({ applied: false });
        if (inflight_ref.current) return inflight_ref.current;
        const body = mode === 'absolute'
            ? { order_id: order.id, tds_absolute: chosen_absolute }
            : { order_id: order.id, tds_percentage: chosen_percentage };
        const p = (async () => {
            try {
                set_applying(true);
                set_page_loader(true, 'Updating TDS...');
                const updated = await general_fetch({ url: 'csp/apply_tds_to_order', body });
                toastr.success('TDS updated — payment total refreshed');
                // No refetch here: run_apply is only reached via the record button's flush(),
                // and the record handlers refresh their own lists after the record succeeds.
                return { applied: true, order: updated };
            } catch (err) {
                console.error('inline apply_tds_to_order failed', err);
                const msg = err && err.err && err.err.message ? err.err.message : '';
                if (msg.includes('PENDING_OFFLINE_PAYMENT_BLOCKS_TDS_EDIT')) {
                    toastr.error('Resolve the pending offline payment proof first.');
                } else if (msg.includes('TDS_ONLY_APPLICABLE_TO_INDIA_ORGS')) {
                    toastr.error('TDS is only applicable to India market orgs.');
                } else if (msg.includes('INVALID_ORDER_TYPE_FOR_TDS')) {
                    toastr.error('TDS can only be edited on renewal, overage, or mid-cycle add-on / license dues.');
                } else if (msg.includes('ORDER_NOT_IN_DUE_STATE')) {
                    toastr.error('This order is not in a due state.');
                } else if (msg.includes('TOO_MUCH_TDS')) {
                    toastr.error('TDS amount cannot exceed the order subtotal (after discount, before tax).');
                } else if (msg.includes('INVALID_TDS_ABSOLUTE') || msg.includes('INVALID_TDS_PARAMS')) {
                    toastr.error('Enter a valid TDS amount.');
                } else {
                    toastr.error('Could not update TDS, please try again.');
                }
                throw err;
            } finally {
                set_page_loader(false);
                set_applying(false);
                inflight_ref.current = null;
            }
        })();
        inflight_ref.current = p;
        return p;
    };
    run_apply_ref.current = run_apply;

    useImperativeHandle(ref, () => ({
        // Awaited by the record button before it records: applies any pending TDS so the
        // record uses the post-TDS total. Resolves { applied, order } or rejects on failure.
        flush: () => (run_apply_ref.current ? run_apply_ref.current() : Promise.resolve({ applied: false })),
    }), []);

    // Breakup reflects the live preview (chosen TDS) while a valid change is pending.
    const preview_order = has_valid_change
        ? {
            ...order,
            tds: preview_tds,
            total: preview_total,
            tds_absolute: mode === 'absolute' ? chosen_absolute : 0,
            tds_percentage: mode === 'percentage' ? chosen_percentage : 0,
        }
        : order;

    if (!editable) {
        // Still render the breakup block if requested, so non-India orgs see a consistent layout.
        return show_breakup ? <BreakupBlock order={order} store_info={store_info} /> : null;
    }

    const adjust_disabled = applying || has_pending_offline_payment;

    const mode_btn_style = (active) => ({
        fontSize: 11,
        padding: '4px 10px',
        cursor: 'pointer',
        border: '1px solid ' + (active ? '#0958d9' : '#cfdcf0'),
        background: active ? '#e8f1ff' : '#fff',
        color: active ? '#0958d9' : '#6b7280',
        fontWeight: active ? 700 : 500,
    });

    return (
        <>
            {show_breakup ? <BreakupBlock order={preview_order} store_info={store_info} /> : null}
            <div
                style={{
                    background: '#f6faff',
                    border: '1px solid #d6e8ff',
                    borderRadius: 6,
                    padding: '10px 12px',
                    margin: '12px 0',
                    opacity: has_pending_offline_payment ? 0.6 : 1,
                }}
                title={has_pending_offline_payment ? 'Resolve the pending offline payment proof before editing TDS.' : undefined}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#0958d9', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Adjust TDS
                    </div>
                    <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={mode_btn_style(mode === 'percentage')} onClick={() => !adjust_disabled && set_mode('percentage')}>%</div>
                        <div style={mode_btn_style(mode === 'absolute')} onClick={() => !adjust_disabled && set_mode('absolute')}>Fixed</div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    {mode === 'percentage' ? (
                        <>
                            <Input
                                type="select"
                                value={selected === null ? '' : selected}
                                onChange={(e) => set_selected(e.target.value === 'custom' ? 'custom' : Number(e.target.value))}
                                disabled={adjust_disabled}
                                style={{ width: 220 }}
                                bsSize="sm"
                            >
                                <option value="" disabled>Select...</option>
                                {PRESETS.map(p => (
                                    <option key={String(p.value)} value={p.value}>{p.label}</option>
                                ))}
                            </Input>
                            {selected === 'custom' ? (
                                <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={custom_value}
                                    onChange={(e) => set_custom_value(e.target.value)}
                                    placeholder="e.g. 5"
                                    disabled={adjust_disabled}
                                    style={{ width: 120 }}
                                    bsSize="sm"
                                />
                            ) : null}
                        </>
                    ) : (
                        <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={absolute_value}
                            onChange={(e) => set_absolute_value(e.target.value)}
                            placeholder={`Amount in ${currency_symbol}`}
                            disabled={adjust_disabled}
                            style={{ width: 220 }}
                            bsSize="sm"
                        />
                    )}
                </div>
                {mode === 'absolute' && chosen_absolute !== null && chosen_absolute > padbt ? (
                    <div style={{ fontSize: 11, color: '#cf1322', marginTop: 6 }}>
                        Amount can't exceed the subtotal ({currency_symbol}{fmt(padbt)}).
                    </div>
                ) : null}
                {has_pending_offline_payment ? (
                    <div style={{ fontSize: 11, color: '#cf1322', marginTop: 6 }}>
                        <i className="fa fa-info-circle mr-1" />
                        A pending offline-payment proof exists for this order — resolve it before editing TDS.
                    </div>
                ) : null}
            </div>
        </>
    );
});

InlineTdsAdjuster.displayName = 'InlineTdsAdjuster';

const BreakupBlock = ({ order, store_info }) => {
    if (!order) return null;
    const currency_symbol = (store_info && store_info.country !== 'IN') ? '$' : '₹';
    const subtotal = Number(order.price_after_discount_before_tax || 0);
    const tax_amount = Number(order.tax || 0);
    const tax_pct = subtotal > 0 ? round2((tax_amount / subtotal) * 100) : 0;
    const tds_amount = Number(order.tds || 0);
    const tds_absolute = Number(order.tds_absolute) || 0;
    const tds_pct = derive_tds_percentage(order);
    const tds_label = tds_absolute > 0 ? 'TDS (fixed)' : `TDS${tds_pct ? ` (${tds_pct}%)` : ''}`;
    const total = Number(order.total || 0);
    return (
        <div style={{ background: '#fafbfc', border: '1px solid #eef0f2', borderRadius: 6, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#1f2933', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                Payment Breakup
            </div>
            <table style={{ width: '100%', fontSize: 13 }}>
                <tbody>
                    <tr>
                        <td style={{ color: '#6b7280' }}>Subtotal (after discount)</td>
                        <td style={{ textAlign: 'right' }}>{currency_symbol}{fmt(subtotal)}</td>
                    </tr>
                    <tr>
                        <td style={{ color: '#6b7280' }}>Tax{tax_pct ? ` (${tax_pct}%)` : ''}</td>
                        <td style={{ textAlign: 'right' }}>+ {currency_symbol}{fmt(tax_amount)}</td>
                    </tr>
                    {tds_amount > 0 ? (
                        <tr>
                            <td style={{ color: '#6b7280' }}>{tds_label}</td>
                            <td style={{ textAlign: 'right', color: '#cf1322' }}>− {currency_symbol}{fmt(tds_amount)}</td>
                        </tr>
                    ) : null}
                    <tr style={{ borderTop: '1px solid #eef0f2' }}>
                        <td style={{ fontWeight: 700, paddingTop: 4 }}>Total Due</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, paddingTop: 4 }}>{currency_symbol}{fmt(total)}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default InlineTdsAdjuster;
